import dayjs from 'dayjs'
import { formatPaymentType, resolvePaymentEntries } from '~/utils/paymentType'

export interface ReceiptDetailModalItem {
  name: string
  qty: number
  price: number
  total: number
  discount: number
  discountText: string
  toppings: { name: string; price: number }[]
  comments: string
}

export interface ReceiptDetailModalModel {
  receiptId: string
  reference: string
  /** Room number typed at payment (shops with the Room setting enabled). NOT a note. */
  room: string
  /** Bill-level comment typed at payment (receipt `Note`). NOT the room. */
  note: string
  dateDisplay: string
  time: string
  guests: number
  paymentTypePrimary: string
  paymentTypeSecondary: string
  paymentLines: { name: string; amount: number }[]
  subTotal: number
  itemDiscount: number
  serviceCharge: number
  additionalDiscount: number
  cardCharge: number
  vat: number
  rounding: number
  grandTotal: number
  shift: string
  cashier: string
  items: ReceiptDetailModalItem[]
  voided: boolean
  voidAt: string
  voidReason: string
}

/**
 * Gross subtotal (BEFORE per-row item discounts) for a STORED receipt.
 * `receipt.SubTotal` is ambiguous (legacy=gross, new app=net); Σ extendedPrice
 * (already includes toppings) is correct in every version. Use this anywhere a
 * stored receipt's subtotal is displayed/aggregated — do NOT read SubTotal directly.
 */
export function grossSubtotalFromReceipt(receipt: Record<string, any>): number {
  const items = receipt?.OrderItems || receipt?.items || []
  if (Array.isArray(items) && items.length > 0) {
    return items.reduce((sum: number, it: any) => {
      const qty = Number(it?.quantity ?? it?.qty ?? 1)
      const price = Number(it?.price ?? 0)
      return sum + Number(it?.extendedPrice ?? (qty * price))
    }, 0)
  }
  const sub = Number(receipt?.SubTotal ?? receipt?.subTotal ?? 0)
  return sub + Math.abs(Number(receipt?.ItemDiscount ?? receipt?.ItemsDiscount ?? 0))
}

/**
 * Net subtotal (AFTER per-row item discounts) for a STORED receipt — Σ discountedPrice.
 * Correct whether the stored SubTotal is gross (legacy) or net (new app), because
 * discountedPrice is the after-item-discount line total in every version. This is the
 * VAT base component and the value the Close-Shop summary needs (gross = net + ItemDiscount).
 */
export function netSubtotalFromReceipt(receipt: Record<string, any>): number {
  const items = receipt?.OrderItems || receipt?.items || []
  if (Array.isArray(items) && items.length > 0) {
    return items.reduce((sum: number, it: any) => {
      const ext = Number(it?.extendedPrice ?? ((Number(it?.quantity ?? it?.qty ?? 1)) * (Number(it?.price) || 0)))
      const disc = Number(it?.discount ?? 0)
      return sum + Number(it?.discountedPrice ?? (ext - disc))
    }, 0)
  }
  // Fallback (no line items): best-effort from stored fields (legacy-gross interpretation).
  return Number(receipt?.SubTotal ?? receipt?.subTotal ?? 0)
    - Math.abs(Number(receipt?.ItemDiscount ?? receipt?.ItemsDiscount ?? 0))
}

/**
 * A receipt's VAT, as reported. READS the value — it does not derive one.
 *
 * This used to recompute VAT fresh from the line items, deliberately ignoring
 * the stored `Vat`, as a self-heal for records a server bug had written wrong.
 * That repair now lives on the server, which applies the shop's billing strategy
 * before the rows are sent (`GET /receipts` → normalizeReceiptsForReport), so by
 * the time a receipt reaches this file its VAT is already correct.
 *
 * Recomputing here is not just redundant, it is WRONG for any shop whose pricing
 * exempts individual lines: the formula assumed VAT applies to the whole bill, so
 * on a divana Product-only bill it turned a correct stored 0 into 38.50 — while
 * the same page's PDF, which went through the server, showed 0.
 *
 * Receipts the POS created locally are equally safe: the SPA resolves the shop's
 * billing strategy when the bill is built, so `Vat` is right at the moment of
 * sale. Both sources agree; there is nothing left to second-guess.
 */
export function receiptVat(receipt: Record<string, any>): number {
  return Number(receipt?.Vat) || 0
}

/**
 * VAT split by mode for a STORED receipt. A single receipt has exactly one VatMode
 * (1 = Inclusive, 2 = Exclusive, else none), so at most one side is non-zero per
 * receipt. But a report aggregates MANY receipts — some inclusive, some exclusive —
 * so the two must be summed and displayed separately, otherwise the inclusive
 * deduction cancels part of the exclusive add-on and misleads.
 *
 * Sign convention (matches how the lines are displayed):
 *  - `exclusive` — VAT added on top of the price. POSITIVE.
 *  - `inclusive` — VAT already embedded in the price, shown as a deduction. NEGATIVE.
 *
 * Magnitude is `receiptVat` — the reported figure, so the split can never
 * disagree with the per-row VAT shown beside it.
 */
export function computeReceiptVatSplit(receipt: Record<string, any>): { exclusive: number; inclusive: number } {
  const mode = Number(receipt?.VatMode || 0)
  const amount = receiptVat(receipt)
  if (!amount) return { exclusive: 0, inclusive: 0 }
  if (mode === 1) return { exclusive: 0, inclusive: -Math.abs(amount) }
  if (mode === 2) return { exclusive: Math.abs(amount), inclusive: 0 }
  return { exclusive: 0, inclusive: 0 }
}

/**
 * Aggregate `computeReceiptVatSplit` over a list of receipts. Returns the summed
 * exclusive (positive) and inclusive (negative) VAT across all receipts.
 */
export function aggregateReceiptVatSplit(receipts: Array<Record<string, any>>): { exclusive: number; inclusive: number } {
  return (receipts || []).reduce(
    (acc, r) => {
      const { exclusive, inclusive } = computeReceiptVatSplit(r)
      acc.exclusive += exclusive
      acc.inclusive += inclusive
      return acc
    },
    { exclusive: 0, inclusive: 0 },
  )
}

export function mapReceiptToReceiptDetailModal(receipt: Record<string, any>): ReceiptDetailModalModel {
  const d = receipt?.Date ? dayjs(receipt.Date) : null
  const paymentEntries = resolvePaymentEntries(receipt as any)
  const items = (receipt?.OrderItems || receipt?.items || []).map((item: any) => {
    const rawTops: any[] = Array.isArray(item?.toppings) ? item.toppings : []
    const toppings = rawTops.map((t: any) => ({
      name: String(t?.name ?? t?.Name ?? ''),
      price: Number(t?.price ?? t?.Price ?? t?.extendedPrice ?? 0),
    })).filter((t) => t.name)

    const qty = Number(item?.quantity ?? item?.qty ?? 1)
    const price = Number(item?.price ?? 0)
    return {
      name: String(item?.name ?? item?.Name ?? ''),
      qty,
      price,
      total: Number(item?.extendedPrice ?? (qty * price)),
      discount: Number(item?.discount ?? 0),
      discountText: String(item?.discountText ?? ''),
      toppings,
      comments: String(item?.comments ?? ''),
    }
  })

  return {
    receiptId: String(receipt?.ReceiptId || receipt?.OrderId || ''),
    reference: String(receipt?.Reference || receipt?.Table || receipt?.table || '-'),
    room: String(receipt?.Room ?? '').trim(),
    note: String(receipt?.Note ?? '').trim(),
    dateDisplay: d?.isValid() ? `${d.format('DD')}/${d.format('MMMM')}/${d.year()}` : '-',
    time: d?.isValid() ? d.format('HH:mm:ss') : '-',
    guests: Number(receipt?.CustomerCount || 0),
    paymentTypePrimary: paymentEntries.length > 1 ? 'Multiple Payment' : (paymentEntries[0]?.name || formatPaymentType(receipt as any) || '-'),
    paymentTypeSecondary: paymentEntries.length > 1
      ? paymentEntries.map((p) => p.amount > 0 ? `${p.name} : ${Number(p.amount).toLocaleString('en-US')}` : p.name).join(', ')
      : '',
    paymentLines: paymentEntries.length > 1
      ? paymentEntries.map((p) => ({ name: p.name, amount: Number(p.amount || 0) }))
      : [],
    subTotal: grossSubtotalFromReceipt(receipt),
    itemDiscount: Number(receipt?.ItemDiscount || receipt?.ItemsDiscount || 0),
    serviceCharge: Number(receipt?.ServiceCharge || 0),
    additionalDiscount: Number(receipt?.Discount || 0),
    cardCharge: Number(receipt?.CardCharge || 0),
    vat: receiptVat(receipt),
    rounding: Number(receipt?.Rounding || receipt?.RoundingV2 || 0),
    grandTotal: Number(receipt?.GrandTotal || 0),
    shift: String(receipt?.ShiftWork || '-'),
    cashier: String(receipt?.User || receipt?.Cashier || '-'),
    items,
    voided: receipt?.IsDeleted === true,
    voidAt: (() => {
      const v = receipt?.voidAt
      if (!v) return ''
      const dv = dayjs(v)
      return dv.isValid() ? dv.format('HH:mm:ss DD-MM-YYYY') : String(v)
    })(),
    voidReason: String(receipt?.voidReason || ''),
  }
}
